import SearchBar from "@/components/atoms/search/searchbar";
import { getPrismicLocale, Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { getAllByIDs, getSingle } from "@/lib/prismic";
import { Content, isFilled } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import Image from "next/image";
import Link from "next/link";
import { notFound } from "next/navigation";
import React from "react";

interface HeroProps {
  locale: Locales;
}

const Hero: React.FC<HeroProps> = async ({ locale }) => {
  const homepageDoc = await getSingle<Content.HomepageDocument>("homepage", locale);
  const homepageData = homepageDoc?.data;

  if (!homepageData) return notFound();

  const validCategories = homepageData.categories?.map((item) => item.category).filter(isFilled.contentRelationship);

  const categoriesList = (await getAllByIDs(
    validCategories.map((rel) => rel.id),
    { lang: getPrismicLocale(locale) }
  )) as unknown as Content.CategorieDocument[];

  return (
    <section
      aria-label="Hero Section"
      className="relative isolate z-10 container mx-auto flex min-h-[600px] w-full flex-col items-start justify-center gap-6 bg-[#0000004c] px-4 py-16 sm:px-8 md:min-h-[700px] md:px-16"
    >
      {homepageData.image.url && (
        <Image
          src={homepageData.image.url}
          alt={homepageData.image.alt || ""}
          fill
          priority
          fetchPriority="high"
          quality={60}
          sizes="(min-width: 1600px) 1600px, 100vw"
          className="-z-10 object-cover"
        />
      )}
      <div className="absolute inset-0 -z-10 bg-black/30" aria-hidden="true" />

      {isFilled.richText(homepageData.title) && (
        <div className="font-cabinet-grotesk flex w-full flex-col items-center self-stretch text-center text-2xl leading-none font-medium text-white sm:text-4xl md:text-5xl md:font-normal lg:text-[60px]">
          <PrismicRichText
            field={homepageData.title}
            components={{
              em: ({ children }) => <em className="font-playfair font-bold italic">{children}</em>,
            }}
          />
        </div>
      )}

      <div className="relative flex h-[60px] w-full items-center gap-0.5 self-stretch">
        <SearchBar locale={locale} />
      </div>

      <div
        aria-label="Category Filters"
        className="relative mx-auto mt-4 hidden w-full max-w-[968px] flex-[0_0_auto] flex-col items-start justify-between self-stretch sm:mt-0 sm:flex-row lg:flex"
      >
        <div className="relative mb-4 flex flex-[0_0_auto] flex-wrap items-start gap-3 sm:mb-0">
          {categoriesList?.map((category, index) => (
            <Link
              key={index}
              href={`${getPermalink(locale, "categories")}/${category.uid}`}
              className="font-cabinet-grotesk rounded border border-solid border-[#ffffff80] bg-[#000c1266] px-1.5 py-1 text-sm leading-[16.7px] font-medium tracking-[0] text-white transition-all duration-200 ease-in-out hover:scale-105 hover:bg-[#000c12cb] active:scale-[98%]"
            >
              {category.data.title}
            </Link>
          ))}
        </div>

        <Link
          href={`${getPermalink(locale, "catalogue")}`}
          className="relative w-fit text-center text-base leading-[19.0px] font-medium tracking-[0] whitespace-nowrap text-white underline transition-all duration-200 ease-in-out hover:scale-105"
        >
          {homepageData.explore_label || __("Explore the catalogue", locale)}
        </Link>
      </div>
    </section>
  );
};

export default Hero;
